import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  saveBlobAsFile,
  extractFileNameFromDisposition,
} from '@/utils/exportUtils';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
  resolveConflicts,
} from '@/utils/apiParamsUtils';

/**
 * Sends a request to create a new employee.
 *
 * - Automatically detects whether the payload is `FormData` or a plain object.
 * - If not FormData, sets `Content-Type` to `application/json`.
 * - Sends a POST request to `/employees/create`.
 *
 * @param {Object|FormData} payload - The data for the new employee. Can be plain JSON or multipart FormData.
 * @returns {Promise<Object>} A promise that resolves with the created employee response data.
 * @throws {Error} Throws an error if the API call fails.
 */
export const createNewEmployee = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.post('/employees/create', payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create a new employee:', error);
    throw error;
  }
};

/**
 * Retrieves all employees.
 * @returns {Promise<Object[]>} A promise that resolves with an array of employee objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllEmployees = async (params) => {
  const filters = prepareFilters(params, {
    firstName: 'default',
    lastName: 'default',
    company: 'default',
    status: 'uppercaseArray',
    createdAt: 'range',
    updatedAt: 'range',
    employeeSector: 'default',
    companies: 'array',
    'generalData.email': 'default',
    'generalData.city': 'default',
    'generalData.personalNumber': 'default',
    'employmentSettings.employmentType': 'default',
    'employmentSettings.startDate': 'default',
    'employmentSettings.endDate': 'default',
    'employmentSettings.isHrOfficer': 'default',
    'employmentSettings.workTimeType': 'default',
    'holidayLeaveCalculation.numberOfHolidayDaysPast': 'default',
    // Carried inside the `filter` JSON; backend reads it to decide whether to
    // include non-employment workers (service contract / author / PP / etc.).
    includeNonEmployment: 'default',
    // Restricts the list to ONLY non-employment workers (dashboard drill-down).
    onlyNonEmployment: 'default',
  });

  const sorters = prepareSorters(params, {
    firstName: 'sortFirstName',
    lastName: 'sortLastName',
    company: 'sortCompany',
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
    city: 'sortCity',
    country: 'sortCountry',
  });

  const pagination = preparePagination(params);

  const conflictRules = [
    { keys: ['company', 'companies'], priority: 'companies' },
  ];

  const filtersResolved = resolveConflicts(filters, conflictRules);

  try {
    const response = await axiosClient.get('/employees', {
      params: {
        filter: filtersResolved,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve employees:', error);
    throw error;
  }
};

/**
 * Retrieves a list of employees for use in dropdown components.
 *
 * Sends a GET request to the `/employees` endpoint with optional query parameters,
 * serializing complex parameters such as `filter`, `type`, and `select` as JSON strings.
 *
 * @async
 * @function
 * @param {Object} options - Options for the request.
 * @param {Object} options.params - Query parameters to include in the request.
 * @param {Object} [options.params.filter] - Optional filter object to narrow down results.
 * @param {string|string[]} [options.params.type] - Optional type(s) of employees to retrieve.
 * @param {string|string[]} [options.params.select] - Optional list of fields to select in the response.
 * @returns {Promise<Object>} - Resolves to the response data containing employee list.
 * @throws Will throw an error if the request fails.
 *
 * @example
 * const employees = await retrieveDropdownAllEmployees({
 *   filter: { isActive: true },
 *   type: ['internal', 'external'],
 *   select: ['id', 'name']
 * });
 */
export const retrieveDropdownAllEmployees = async (params) => {
  try {
    const response = await axiosClient.get('/employees', {
      params: {
        filter: params?.filter,
        select: params?.select,
        ...(params?.limit ? { limit: params.limit } : {}),
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.select) {
          params.select = JSON.stringify(params.select);
        }

        if (params.type) {
          params.type = JSON.stringify(params.type);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve users:', error);
    throw error;
  }
};

/**
 * Sends a request to update an existing employee by their ID.
 *
 * - Automatically detects whether the payload is `FormData` or plain JSON.
 * - If not FormData, sets `Content-Type` header to `application/json`.
 * - Sends a PUT request to `/employees/{employeeId}`.
 *
 * @param {string} employeeId - The unique identifier of the employee to update.
 * @param {Object|FormData} payload - The updated data for the employee.
 * @returns {Promise<Object>} A promise that resolves with the response data after successful update.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateEmployeeById = async (employeeId, payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.put(
      `/employees/${employeeId}`,
      payload,
      {
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to update employee with ID ${employeeId}:`, error);
    throw error;
  }
};

/**
 * Retrieves a employee by its ID.
 * @param {string} employeeId - The unique identifier of the employee.
 * @returns {Promise<Object>} A promise that resolves with the employee data.
 * @throws Will throw an error if the request fails.
 */
export const getEmployeeById = async (employeeId) => {
  try {
    const response = await axiosClient.get(`/employees/${employeeId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve employee with ID ${employeeId}:`, error);
    throw error;
  }
};

// Calendar-managed absence log for non-employment workers. Replaces the whole
// specialAbsence.entries array (each entry: { from, to, type, note }).
export const updateEmployeeAbsence = async (employeeId, entries) => {
  const response = await axiosClient.put(`/employees/${employeeId}/absence`, {
    entries,
  });
  return response.data?.data;
};

/**
 * Sets the employee's photo (JPG/PNG/WebP, max 3 MB). The same picture becomes
 * the linked account's avatar.
 * @param {string} employeeId
 * @param {File} file
 * @returns {Promise<Object>} `{ message, data: { photo: { key } } }`
 */
export const uploadEmployeePhoto = async (employeeId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await axiosClient.put(
    `/employees/${employeeId}/photo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * Removes the employee's photo (and the linked account's avatar).
 * @param {string} employeeId
 * @returns {Promise<Object>}
 */
export const removeEmployeePhoto = async (employeeId) => {
  const response = await axiosClient.delete(`/employees/${employeeId}/photo`);
  return response.data;
};

/**
 * Deletes an employee by their ID.
 * @param {string} employeeId - The unique identifier of the employee.
 * @returns {Promise<Object>} A promise that resolves when the employee is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteEmployeeById = async (employeeId) => {
  try {
    const response = await axiosClient.delete(`/employees/${employeeId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete employee with ID ${employeeId}:`, error);
    throw error;
  }
};

/**
 * Retrieves employee holiday calculation based on the provided payload.
 * @param {Object} payload - The data to be sent with the request.
 * @returns {Promise<Object>} A promise that resolves with the holiday calculation data.
 * @throws Will throw an error if the request fails.
 */
export const retrieveEmployeeHolidayCalculation = async (payload) => {
  try {
    const response = await axiosClient.post(
      `/employees/holiday/calculation`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to retrieve holiday calculation:`, error);
    throw error;
  }
};

/**
 * Retrieves employment-related employees based on the provided payload.
 * @param {{ companyId: string, type: string }} payload - The data to be sent with the request.
 * @returns {Promise<Object>} A promise that resolves with the list of employees.
 * @throws Will throw an error if the request fails.
 */
export const retrieveEmployeeEmploymentEmployees = async (payload) => {
  try {
    const response = await axiosClient.post(
      `/employees/employment/employees`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve employment employees:', error);
    throw error;
  }
};

/**
 * Uploads employee data extracted from Excel to the backend.
 *
 * Sends a POST request to `/employees/employment/upload` containing:
 * - `excelData`: parsed array of employees from uploaded Excel
 * - `company`: selected company ID
 *
 * The backend validates records, stores new employees, and returns a response with
 * optional `failedEmployees` array if duplicates or invalid records exist.
 *
 * @param {Array<Object>} payload.excelData - Array of employee records parsed from Excel.
 * @param {string} payload.company - Company ID.
 * @returns {Promise<Object>} Response containing a status message and optionally failed employees.
 * @throws {Error} On request failure or network error.
 * @param formData
 */
export const uploadEmployees = async (formData) => {
  try {
    const response = await axiosClient.post(`/employees/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload employees:', error);
    throw error;
  }
};

/**
 * Retrieves employee holiday availability calculation for a specific date.
 *
 * This is used to calculate how many holiday days an employee would have available
 * at a specific point in time (e.g., when selecting leave dates in the future).
 *
 * @param {Object} payload - The calculation parameters.
 * @param {string} payload.startDate - Employee's employment start date.
 * @param {string} [payload.endDate] - Employee's employment end date (optional).
 * @param {number} payload.numberOfHolidayDaysPast - Holiday days carried over from previous year.
 * @param {number} payload.numberOfHolidayDaysUsed - Holiday days already used.
 * @param {number} payload.numberOfHolidayDaysContract - Total holiday days per contract.
 * @param {string} payload.currentDate - The date for which to calculate availability.
 * @returns {Promise<Object>} A promise that resolves with the calculation result.
 * @throws Will throw an error if the request fails.
 */
export const retrieveHolidayAvailabilityCalculation = async (payload) => {
  try {
    const response = await axiosClient.post(
      `/employees/holiday/calculation/availability`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to retrieve holiday availability calculation:`,
      error
    );
    throw error;
  }
};

/**
 * Sends a request to update employment status for an employee (e.g. termination or extension).
 *
 * - Automatically detects whether the payload is `FormData` or plain JSON.
 * - If not FormData, sets `Content-Type` header to `application/json`.
 * - Sends a POST request to `/employees/employment`.
 *
 * @param {Object|FormData} payload - The employment update data (must include `employeeId` and `type`).
 * @returns {Promise<Object>} A promise that resolves with the server response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateEmploymentStatus = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.post('/employees/employment', payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to update employment status:', error);
    throw error;
  }
};

/**
 * Sends bulk invitation emails to employees.
 *
 * This service allows admins to send or resend welcome/invitation emails
 * to multiple employees. Each employee will receive an email with a unique
 * link to set their password.
 *
 * @param {string[]} employeeIds - Array of employee IDs to send invitations to.
 * @returns {Promise<Object>} A promise that resolves with the result containing success, failed, and skipped counts.
 * @throws {Error} Throws an error if the request fails.
 */
export const sendBulkInvitations = async (employeeIds) => {
  try {
    const response = await axiosClient.post('/employees/bulk/invitations', {
      employeeIds,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to send bulk invitations:', error);
    throw error;
  }
};

/**
 * Sends an invitation email to a single employee.
 *
 * @param {string} employeeId - The employee ID to send invitation to.
 * @returns {Promise<Object>} A promise that resolves with the result.
 * @throws {Error} Throws an error if the request fails.
 */
export const sendEmployeeInvitation = async (employeeId) => {
  return sendBulkInvitations([employeeId]);
};

export const addAdditionalHolidayDays = async (employeeId, payload) => {
  try {
    const { data } = await axiosClient.post(
      `/employees/${employeeId}/additional-holiday-days`,
      payload
    );
    return data;
  } catch (error) {
    console.error('Failed to add additional holiday days:', error);
    throw error;
  }
};

export const getAdditionalHolidayDays = async (employeeId, year) => {
  try {
    const params = year ? { year } : {};
    const { data } = await axiosClient.get(
      `/employees/${employeeId}/additional-holiday-days`,
      { params }
    );
    return data;
  } catch (error) {
    console.error('Failed to get additional holiday days:', error);
    throw error;
  }
};

export const getEmployeeCareerTimeline = async (employeeId) => {
  try {
    const { data } = await axiosClient.get(
      `/employees/${employeeId}/career-timeline`
    );
    return data;
  } catch (error) {
    console.error('Failed to get employee career timeline:', error);
    throw error;
  }
};

/**
 * Retrieves the annual-leave (GO) history for an employee: realized
 * holiday-leave requests merged with raw balance adjustments, plus a chart
 * series of total/past/current over time.
 *
 * @param {string} employeeId
 * @param {string|number} [year] - Optional year filter.
 * @returns {Promise<Object>}
 */
export const getEmployeeHolidayHistory = async (employeeId, year) => {
  try {
    const { data } = await axiosClient.get(
      `/employees/${employeeId}/holiday-history`,
      { params: year ? { year } : {} }
    );
    return data;
  } catch (error) {
    console.error('Failed to get employee holiday history:', error);
    throw error;
  }
};

/**
 * Retrieves leave history (all types except annual leave) for a single employee,
 * with per-type / seasonality / duration aggregates for the charts.
 *
 * @param {string} employeeId
 * @param {{year?: string|number, type?: string}} [filters]
 * @returns {Promise<Object>}
 */
export const getEmployeeOtherLeavesHistory = async (employeeId, filters = {}) => {
  try {
    const params = {};
    if (filters.year) params.year = filters.year;
    if (Array.isArray(filters.types) && filters.types.length)
      params.types = filters.types.join(',');
    if (filters.includeHoliday) params.includeHoliday = true;
    const { data } = await axiosClient.get(
      `/employees/${employeeId}/other-leaves-history`,
      { params }
    );
    return data;
  } catch (error) {
    console.error('Failed to get employee other leaves history:', error);
    throw error;
  }
};

/**
 * Company-wide "other leaves" overview (matrix or list) with charts.
 * @param {Object} params - { company, employeeSector, search, types[], year, month, view, page, limit }
 */
export const getLeavesOverview = async (params = {}) => {
  const q = { ...params };
  if (Array.isArray(q.types)) q.types = q.types.join(',');
  try {
    const { data } = await axiosClient.get('/employees/leaves-overview', {
      params: q,
    });
    return data;
  } catch (error) {
    console.error('Failed to get leaves overview:', error);
    throw error;
  }
};

/**
 * "First part of annual leave by 31.12." list of one company (Art. 73).
 * @param {Object} params - { company, search, statuses[] }
 */
export const getHolidayFirstPartOverview = async (params = {}) => {
  const q = { ...params };
  if (Array.isArray(q.statuses)) q.statuses = q.statuses.join(',');
  try {
    const { data } = await axiosClient.get(
      '/employees/leaves-overview/first-part',
      { params: q }
    );
    return data;
  } catch (error) {
    console.error('Failed to get first part overview:', error);
    throw error;
  }
};

/** Excel export of the leaves overview (respects the same filters). */
export const exportLeavesOverview = async (params = {}) => {
  const q = { ...params };
  if (Array.isArray(q.types)) q.types = q.types.join(',');
  try {
    const response = await axiosClient.get('/employees/leaves-overview/export', {
      params: q,
      responseType: 'blob',
    });
    const contentType =
      response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName =
      extractFileNameFromDisposition(response.headers['content-disposition']) ||
      'pregled-odsustva.xlsx';
    saveBlobAsFile(response.data, contentType, fileName);
  } catch (error) {
    console.error('Failed to export leaves overview:', error);
    throw error;
  }
};

/** Excel of the first-part list (same params as getHolidayFirstPartOverview). */
export const exportHolidayFirstPart = async (params = {}) => {
  const q = { ...params };
  if (Array.isArray(q.statuses)) q.statuses = q.statuses.join(',');
  try {
    const response = await axiosClient.get(
      '/employees/leaves-overview/first-part/export',
      { params: q, responseType: 'blob' }
    );
    const contentType =
      response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName =
      extractFileNameFromDisposition(response.headers['content-disposition']) ||
      'prvi-deo-go.xlsx';
    saveBlobAsFile(response.data, contentType, fileName);
  } catch (error) {
    console.error('Failed to export first part overview:', error);
    throw error;
  }
};

/**
 * Read-only absence statistics for the employee profile (calendar + charts).
 *
 * @param {string} employeeId
 * @param {number} [year]
 * @returns {Promise<Object>}
 */
export const getEmployeeProfileStats = async (employeeId, year) => {
  try {
    const { data } = await axiosClient.get(
      `/employees/${employeeId}/profile-stats`,
      { params: year ? { year } : {} }
    );
    return data;
  } catch (error) {
    console.error('Failed to get employee profile stats:', error);
    throw error;
  }
};

/**
 * The termination in effect for an employee (reason, documents, notices sent,
 * offered recipients), or `{ termination: null }`.
 *
 * @param {string} employeeId
 * @returns {Promise<{ termination: Object|null }>}
 */
export const getCurrentTermination = async (employeeId) => {
  const { data } = await axiosClient.get(
    `/employees/employment/termination/${employeeId}`
  );
  return data;
};

/**
 * Emails the termination notice (no attachment) to the chosen recipients.
 *
 * @param {string} employeeId
 * @param {{ contractId: string, recipients: string[] }} payload
 */
export const notifyTermination = async (employeeId, payload) => {
  const { data } = await axiosClient.post(
    `/employees/employment/termination/${employeeId}/notify`,
    payload
  );
  return data;
};

/**
 * Undoes a termination entered without a document of its own.
 *
 * @param {string} employeeId
 * @param {string} contractId
 */
export const deleteTermination = async (employeeId, contractId) => {
  const { data } = await axiosClient.delete(
    `/employees/employment/termination/${employeeId}/${contractId}`
  );
  return data;
};

const EmployeesService = {
  createNewEmployee,
  retrieveAllEmployees,
  updateEmployeeById,
  getEmployeeById,
  deleteEmployeeById,
  retrieveEmployeeHolidayCalculation,
  retrieveHolidayAvailabilityCalculation,
  retrieveEmployeeEmploymentEmployees,
  retrieveDropdownAllEmployees,
  uploadEmployees,
  updateEmploymentStatus,
  getCurrentTermination,
  notifyTermination,
  deleteTermination,
  sendBulkInvitations,
  sendEmployeeInvitation,
  addAdditionalHolidayDays,
  getAdditionalHolidayDays,
  getEmployeeCareerTimeline,
  getEmployeeHolidayHistory,
  getEmployeeOtherLeavesHistory,
  getEmployeeProfileStats,
  getLeavesOverview,
  exportLeavesOverview,
  exportHolidayFirstPart,
  getHolidayFirstPartOverview,
};

export default EmployeesService;
